import { useAmplifyREST } from "hooks/useAmplifyREST";
import { useEffect, useState, useCallback } from "react";
import {
  Card, Col, Row, Form, Button
} from "react-bootstrap";
import IHwrcSite from "interfaces/IHwrcSite";
import {
  DatePickerComponent,
  TimePickerComponent
} from "@syncfusion/ej2-react-calendars";
import { CheckSquare, Square } from "react-bootstrap-icons";

import "./SiteDetails.scss";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import siteDiarySiteDetailsSchema from "modules/schemas/siteDiarySiteDetailsSchema";
import IUpsertHwrcSiteDiaryRequestModel from "interfaces/Request/IUpsertHwrcSiteDiaryRequestModel";
import { format } from "date-fns";
import IHwrcSiteDiaryFormDetails from "interfaces/IHwrcSiteDiaryFormDetails";
import ISiteDiaryViewModel from "interfaces/SiteDiary/ISiteDiaryViewModel";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { useStateMachine } from "little-state-machine";
import { RolesEnum } from "modules/rolesEnum";
import { convertToLocalDate } from "helpers/DateUtils";
import { ContinueConfirmation } from "components/ContinueConfirmation/ContinueConfirmation";
import { makeDateRangePickerAACompliant } from "modules/SyncfusionAAFixer";
import WeatherCustomIcon from "components/WeatherCustomIcons/WeatherCustomIcon";
import { Spinner } from "@whitespace/whitespace-ui";

interface SiteDetailsProp {
  siteDiary: ISiteDiaryViewModel | undefined;
  diarySave: (hwrcSiteId: string, selectedDate: Date) => void;
  diaryLoad: (hwrcSiteId: string, selectedDate: Date) => void;
  onHwrcSiteChanged: (hwrcSiteId: string) => void;
  onSelectedDateChanged: (selectedDate: Date) => void;
  selectedDateOverride?: Date;
  hwrcSiteIdOverride?: string;
}

const SiteDetails = ({
  siteDiary,
  diarySave,
  diaryLoad,
  onHwrcSiteChanged,
  onSelectedDateChanged,
  selectedDateOverride,
  hwrcSiteIdOverride
}: SiteDetailsProp) => {
  const {
    register, handleSubmit, getValues, setValue, watch
  } = useForm<IHwrcSiteDiaryFormDetails>({
    resolver: yupResolver(siteDiarySiteDetailsSchema),
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const [aaFixDateRangePickerCompleted, setAAFixDateRangePickerCompleted] = useState<boolean>(false);

  const now = new Date();
  const [minDate, setMinDate] = useState<Date>();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    selectedDateOverride || now
  );
  const [hwrcSites, setHwrcSites] = useState<IHwrcSite[]>();
  const [saveButtonDisabled, setSaveButtonDisabled] = useState<boolean>(true);
  const [gatesOpenRef, setGatesOpenRef] = useState<
  TimePickerComponent | undefined
  >();
  const [initialGatesOpened, setInitialGatesOpened] = useState<Date>();

  const [gatesClosedRef, setGatesClosedRef] = useState<
  TimePickerComponent | undefined
  >();
  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [showGateTimesError, setShowGateTimesError] = useState<boolean>(false);
  const [canEdit, setCanEdit] = useState<boolean>(true);
  const [onInitComplete, setOnInitComplete] = useState<boolean>(false);
  const [showContinueConfirmation, setShowContinueConfirmation] = useState<boolean>(false);
  const { state } = useStateMachine();
  const [hasHwrcSiteChanged, setHasHwrcSiteChanged] = useState<boolean>(false);

  const [previousHwrcSiteId, setPreviousHwrcSiteId] = useState<string>("");
  const [previousSelectedDate, setPreviousSelectedDate] = useState<Date>(now);
  const [selectedWeatherIcon, setSelectedWeatherIcon] = useState<string|undefined>();

  const {
    data: getHwrcSitesData,
    sendRequest: getHwrcSitesSendRequest,
    errors: getHwrcSitesErrors,
    isSending: getHwrcSitesIsSending
  } = useAmplifyREST();

  const {
    sendRequest: upsertDiarySendRequest,
    statusCode: upsertDiaryStatusCode,
    errors: upsertDiaryErrors,
    isSending: upsertDiaryIsSending,
    resetState: upsertDiaryResetState
  } = useAmplifyREST();

  const apiErrors = getHwrcSitesErrors || upsertDiaryErrors;
  const isSending = getHwrcSitesIsSending
    || upsertDiaryIsSending
    || !aaFixDateRangePickerCompleted;

  const watchFields = watch(["weather"]);

  useEffect(() => {
    getHwrcSitesSendRequest("settings/hwrcsites/active", "GET");
  }, [getHwrcSitesSendRequest]);

  useEffect(() => {
    const minDateCalc = new Date();
    minDateCalc.setDate(minDateCalc.getDate() - 30);
    setMinDate(minDateCalc);
  }, []);

  const setGatesOpened = () => {
    if (siteDiary?.isEmpty) {
      return;
    }
    if (siteDiary && siteDiary.reportTimes.length > 0) {
      setInitialGatesOpened(siteDiary.gatesOpened);
    } else if (getValues("hwrcSiteId") && canEdit) {
      setInitialGatesOpened(new Date());
    } else {
      setInitialGatesOpened(undefined);
    }
  };

  useEffect(() => {
    setGatesOpened();
    if (siteDiary) {
      setValue("weather", siteDiary?.weatherCondition ?? "");
      if (hwrcSiteIdOverride) {
        setValue("hwrcSiteId", hwrcSiteIdOverride);
      }
    }
  }, [siteDiary]);

  useEffect(() => {
    if (selectedDate) {
      setCanEdit(
        !(
          state.userRole === RolesEnum.Site
          && convertToLocalDate(selectedDate) !== convertToLocalDate(now)
        )
      );
    }
  }, [selectedDate, state]);

  useEffect(() => {
    if (apiErrors) {
      setShowContinueConfirmation(false);
    }
  }, [apiErrors]);

  const renderHwrcSites = () => {
    const hwrcSiteOptions = [
      <option key="" value="">
        Please Select
      </option>
    ];

    if (hwrcSites) {
      hwrcSites.forEach(hwrcSite => {
        hwrcSiteOptions.push(
          <option key={hwrcSite.id} value={hwrcSite.id}>
            {hwrcSite.name}
          </option>
        );
      });
    }

    return hwrcSiteOptions;
  };

  const renderStatusReports = () => siteDiary?.reportTimes.map((reportTime, idx) => {
    if (!selectedDate) {
      return <></>;
    }

    const reportTimeCalc = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      reportTime.getHours(),
      reportTime.getMinutes(),
      reportTime.getSeconds()
    );

    const selectedDateCalc = selectedDate.toLocaleDateString() === now.toLocaleDateString()
      ? now
      : reportTimeCalc;

    return (
      <Row key={`statusReport_${idx}`} data-testid="regionReportTimes">
        <Col
          className={
            !siteDiary.submittedReportTimes[idx]
              && selectedDateCalc >= reportTimeCalc
              ? "report-time-expired"
              : ""
          }
        >
          {format(reportTime, "HH:mm:ss")}
        </Col>
        <Col>
          {siteDiary.submittedReportTimes[idx] ? (
            <CheckSquare data-testid="reportSubmittedCheck" />
          ) : (
            <Square data-testid="reportSubmittedNotChecked" />
          )}
        </Col>
        <Col>
          {siteDiary.submittedReportTimes[idx]
              && format(siteDiary.submittedReportTimes[idx], "HH:mm:ss")}
        </Col>
      </Row>
    );
  });

  const renderAdditionalSubmittedReports = () => {
    if (
      siteDiary!.submittedReportTimes.length <= siteDiary!.reportTimes.length
    ) {
      return <></>;
    }

    return siteDiary!.submittedReportTimes
      .slice(siteDiary!.reportTimes.length)
      .map((submittedReportTime, idx) => (
        <Row key={`submittedReportTime_${idx}`}>
          <Col />
          <Col />
          <Col>{format(submittedReportTime, "HH:mm:ss")}</Col>
        </Row>
      ));
  };

  const handleUpsertHwrcSiteClick = (
    formDetails: IHwrcSiteDiaryFormDetails
  ) => {
    const request: IUpsertHwrcSiteDiaryRequestModel = {
      weather: formDetails.weather
    };

    if (gatesOpenRef!.value) {
      request.gatesOpen = gatesOpenRef!.value.toISOString();
    }

    if (gatesClosedRef!.value) {
      // eslint-disable-next-line max-len
      request.gatesClosed = gatesClosedRef!.value.toISOString();
    }

    // eslint-disable-next-line max-len
    upsertDiarySendRequest(
      `hwrcsitediary/hwrcsite/${
        formDetails.hwrcSiteId
      }/diary/${convertToLocalDate(formDetails.specificDate || selectedDate!)}`,
      "POST",
      request
    );
  };

  const handleConfirmationSave = () => {
    upsertDiaryResetState();

    const request: IHwrcSiteDiaryFormDetails = {
      hwrcSiteId: hasHwrcSiteChanged
        ? previousHwrcSiteId
        : getValues("hwrcSiteId"),
      weather: getValues("weather"),
      gatesOpen: "",
      gatesClosed: "",
      specificDate: previousSelectedDate
    };

    handleUpsertHwrcSiteClick(request);
  };

  const loadDiary = () => {
    if (getValues("hwrcSiteId") && selectedDate) {
      diaryLoad(getValues("hwrcSiteId"), selectedDate);
      setHasHwrcSiteChanged(false);
      setShowContinueConfirmation(false);
    }
  };

  useEffect(() => {
    if (upsertDiaryStatusCode === 200) {
      diarySave(getValues("hwrcSiteId"), selectedDate!);
      loadDiary();
      setHasHwrcSiteChanged(false);
      setShowContinueConfirmation(false);
      upsertDiaryResetState();
    }
  }, [upsertDiaryStatusCode]);

  useEffect(() => {
    if (gatesOpenRef && gatesClosedRef) {
      makeDateRangePickerAACompliant(true);
      setAAFixDateRangePickerCompleted(true);
    }
  }, [gatesOpenRef, gatesClosedRef]);

  useEffect(() => {
    if (getHwrcSitesData) {
      setHwrcSites(getHwrcSitesData.hwrcSites);
      if (getHwrcSitesData.hwrcSites.length === 1) {
        setPreviousHwrcSiteId(getHwrcSitesData.hwrcSites[0].id);
        setValue("hwrcSiteId", getHwrcSitesData.hwrcSites[0].id);
        loadDiary();
        onHwrcSiteChanged(getHwrcSitesData.hwrcSites[0].id);
      }
    }
  }, [getHwrcSitesData]);

  const canFormBeSubmitted = (): boolean => {
    const hwrcSiteId = getValues("hwrcSiteId");
    const weather = getValues("weather");
    const requiredFieldsEntered = hwrcSiteId !== undefined
      && hwrcSiteId !== ""
      && hwrcSiteId.length > 0
      && selectedDate !== undefined;

    const gatesOpenEntry = gatesOpenRef?.value ? Number(format(gatesOpenRef.value, "HHmmSS")) : undefined;
    const gatesClosedEntry = gatesClosedRef?.value ? Number(format(gatesClosedRef.value, "HHmmSS")) : undefined;

    const hasWeatherChanged = ((weather.length > 0 && siteDiary?.weatherCondition && weather !== siteDiary?.weatherCondition)
    || (weather.length === 0 && siteDiary?.weatherCondition && siteDiary?.weatherCondition.length > 0)
    || (weather.length > 0 && !siteDiary?.weatherCondition)
    );

    const atLeastOneFieldChangedValue = hasWeatherChanged
      || gatesOpenRef?.value?.getTime() !== siteDiary?.gatesOpened?.getTime()
      || gatesClosedRef?.value?.getTime() !== siteDiary?.gatesClosed?.getTime();

    if (gatesOpenEntry
      && gatesClosedEntry
      && gatesClosedEntry <= gatesOpenEntry
    ) {
      setShowGateTimesError(true);
      setSaveButtonDisabled(true);
      return false;
    }

    setShowGateTimesError(false);

    const canBeSubmitted = requiredFieldsEntered && (atLeastOneFieldChangedValue || (siteDiary && siteDiary.reportTimes.length === 0)!);
    setSaveButtonDisabled(!canBeSubmitted);
    return canBeSubmitted;
  };

  useEffect(() => {
    if (
      selectedDate
      && (format(selectedDate, "yyyyMMdd") !== format(previousSelectedDate, "yyyyMMdd") || hasHwrcSiteChanged || !onInitComplete)
    ) {
      setOnInitComplete(true);
      const unsavedChanges = canFormBeSubmitted() && siteDiary && siteDiary.reportTimes.length > 0;

      if (
        !unsavedChanges
        && (previousHwrcSiteId !== getValues("hwrcSiteId")
          || previousSelectedDate.toLocaleDateString()
            !== selectedDate.toLocaleDateString())
      ) {
        loadDiary();
        setShowContinueConfirmation(false);
        setHasHwrcSiteChanged(false);
        return;
      }

      if (getValues("hwrcSiteId") && selectedDate) {
        setShowContinueConfirmation(true);
      }
    }
  }, [selectedDate, hasHwrcSiteChanged]);

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  useEffect(() => {
    if (siteDiary && gatesOpenRef && gatesClosedRef && selectedDate) {
      canFormBeSubmitted();
    }
  }, [watchFields, siteDiary, gatesOpenRef, gatesClosedRef, selectedDate]);

  useEffect(() => {
    const weather = getValues("weather");

    if (weather && weather !== "") {
      setSelectedWeatherIcon(weather);
    } else {
      setSelectedWeatherIcon(undefined);
    }
  }, [watchFields, setSelectedWeatherIcon]);

  return (
    <>
      <Card className="card-site-details">
        <Spinner
          animation="grow"
          visible={isSending}
          spinnerText="Loading"
          variant="info"
        />
        {showExceptions && apiErrors && (
          <Card.Header>
            <AmplifyError
              errors={apiErrors}
              isDismissable
              onCloseHandler={exceptionAlertCloseHandler}
            />
          </Card.Header>
        )}
        <Form
          autoComplete="off"
          onSubmit={handleSubmit(handleUpsertHwrcSiteClick)}
        >
          {siteDiary && (
            <Card.Body className="no-padding">
              <Row className="mb-2 ms-2 mt-2 w-auto align-items-start">
                <Col xs={12} lg={8}>
                  <Row>
                    <span className="d-flex justify-content-center w-100 mb-2 site-details-header">
                      Site Details
                    </span>
                    <Row className="mb-2">
                      <Col xs={3}>
                        <Form.Label
                          id="hwrcSiteLabel"
                          className="horiz-form-label"
                        >
                          Site:
                        </Form.Label>
                      </Col>
                      <Col>
                        {hwrcSites && hwrcSites.length === 1 && (
                          <>
                            <Form.Control
                              data-testid="hdnHwrcSite"
                              type="hidden"
                              role="textbox"
                              defaultValue={hwrcSites[0].id}
                              {...register("hwrcSiteId")}
                            />
                            <Form.Label>{hwrcSites[0].name}</Form.Label>
                          </>
                        )}
                        {hwrcSites && hwrcSites.length > 1 && (
                          <Form.Select
                            data-testid="ddlHwrcSite"
                            aria-required
                            aria-invalid={false}
                            aria-labelledby="hwrcSiteLabel"
                            {...register("hwrcSiteId", {
                              onChange: () => {
                                setHasHwrcSiteChanged(true);
                                onHwrcSiteChanged(getValues("hwrcSiteId"));
                              }
                            })}
                            onChangeCapture={() => {
                              setPreviousHwrcSiteId(getValues("hwrcSiteId"));
                            }}
                          >
                            {renderHwrcSites()}
                          </Form.Select>
                        )}
                      </Col>
                    </Row>
                    <Row className="mb-2">
                      <Col xs={3}>
                        <Form.Label id="dateLabel" className="horiz-form-label">
                          Date:
                        </Form.Label>
                      </Col>
                      <Col>
                        <DatePickerComponent
                          format="dd/MM/yyyy"
                          strictMode
                          value={selectedDate}
                          min={minDate}
                          max={now}
                          data-testid="diaryDate"
                          showClearButton={false}
                          htmlAttributes={{
                            "aria-labelledby": "dateLabel",
                            "aria-required": "true"
                          }}
                          change={(dtp: any) => {
                            if (dtp.value !== null) {
                              setSelectedDate(dtp.value);
                              onSelectedDateChanged(dtp.value);
                            } else {
                              const dateToSet = previousSelectedDate !== null
                                ? previousSelectedDate
                                : now;
                              setSelectedDate(undefined);
                              setSelectedDate(dateToSet);
                              const hwrcSiteId = getValues("hwrcSiteId");

                              if (hwrcSiteId) {
                                diaryLoad(getValues("hwrcSiteId"), dateToSet);
                              }
                            }
                          }}
                          onChange={() => {
                            setPreviousSelectedDate(selectedDate!);
                          }}
                        />
                      </Col>
                    </Row>
                    <Row className="mb-2">
                      <Col>
                        <Form.Label
                          id="gatesOpenLabel"
                          className="horiz-form-label"
                        >
                          Gates Open:
                        </Form.Label>
                      </Col>
                      <Col>
                        <TimePickerComponent
                          ref={(t: TimePickerComponent) => {
                            setGatesOpenRef(t);
                          }}
                          data-testid="gatesOpen"
                          id="gatesOpen"
                          type="text"
                          htmlAttributes={{
                            "aria-labelledby": "gatesOpenLabel"
                          }}
                          format="HH:mm"
                          value={initialGatesOpened}
                          onChange={() => {
                            canFormBeSubmitted();
                          }}
                          showClearButton={false}
                          enabled={canEdit}
                        />
                      </Col>
                      <Col>
                        <Form.Label
                          id="gatesClosedLabel"
                          className="horiz-form-label"
                        >
                          Gates Closed:
                        </Form.Label>
                      </Col>
                      <Col>
                        <TimePickerComponent
                          ref={(t: TimePickerComponent) => {
                            setGatesClosedRef(t);
                          }}
                          data-testid="gatesClosed"
                          id="gatesClosed"
                          type="text"
                          htmlAttributes={{
                            "aria-labelledby": "gatesClosedLabel"
                          }}
                          format="HH:mm"
                          value={siteDiary!.gatesClosed}
                          onChange={() => {
                            canFormBeSubmitted();
                          }}
                          showClearButton={false}
                          enabled={canEdit}
                        />
                        {showGateTimesError && (
                          <span className="validation-error">
                            Must be after opening time
                          </span>
                        )}
                      </Col>
                    </Row>
                    <Row className="mb-2">
                      <Col xs={3}>
                        <Form.Label
                          id="weatherLabel"
                          className="horiz-form-label"
                        >
                          Weather:
                        </Form.Label>
                      </Col>
                      <Col>
                        <div className="d-flex w-100 align-items-center">
                          <div className="d-flex w-100">
                            <Form.Select
                              {...register("weather", {
                                onChange: () => {
                                  canFormBeSubmitted();
                                }
                              })}
                              data-testid="ddlWeather"
                              aria-required
                              aria-invalid={false}
                              aria-labelledby="weatherLabel"
                              className="weather-selector"
                              disabled={!canEdit}
                            >
                              <option key="" value="">
                                Please Select
                              </option>
                              <option key="Sunny" value="Sunny">
                                Sunny
                              </option>
                              <option key="PartlyCloudy" value="PartlyCloudy">
                                Partly Cloudy
                              </option>
                              <option key="Cloudy" value="Cloudy">
                                Cloudy
                              </option>
                              <option key="LightRain" value="LightRain">
                                Light Rain
                              </option>
                              <option key="HeavyRain" value="HeavyRain">
                                Heavy Rain
                              </option>
                              <option key="Stormy" value="Stormy">
                                Stormy
                              </option>
                              <option key="Windy" value="Windy">
                                Windy
                              </option>
                              <option key="Sleet" value="Sleet">
                                Sleet
                              </option>
                              <option key="LightSnow" value="LightSnow">
                                Light Snow
                              </option>
                              <option key="HeavySnow" value="HeavySnow">
                                Heavy Snow
                              </option>
                              <option key="VeryHot" value="VeryHot">
                                Very Hot
                              </option>
                              <option key="VeryCold" value="VeryCold">
                                Very Cold
                              </option>
                            </Form.Select>
                          </div>
                          <div
                            data-testid="selectedWeather"
                            className="d-none ms-2 mt-2 d-xl-flex justify-content-end"
                          >
                            { selectedWeatherIcon && (
                              <WeatherCustomIcon
                                id={selectedWeatherIcon}
                                weatherIconId={selectedWeatherIcon}
                              />
                            )}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Row>
                </Col>
                <Col xs={12} lg={4}>
                  <Row>
                    <Col>
                      <span className="d-flex justify-content-center mb-2 site-details-header">
                        Status Reports
                      </span>
                      <Row>
                        <Col className="status-reports-wrapper">
                          <div className="status-reports">
                            {renderStatusReports()}
                            {renderAdditionalSubmittedReports()}
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className="align-items-center">
                <Col xs={8}>
                  <span
                    className="ms-2"
                    data-testid="siteInspectionsSubmissionCounter"
                  >
                    Site Inspections Submitted:
                    {" "}
                    {siteDiary.submittedReportTimes?.length}
                  </span>
                </Col>
                <Col xs={4} className="d-flex flex-wrap justify-content-end">
                  <Button
                    type="submit"
                    data-testid="btnSave"
                    className="me-2 mb-2"
                    aria-label="Save Diary"
                    disabled={saveButtonDisabled || !canEdit}
                  >
                    Save
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          )}
        </Form>
      </Card>
      <ContinueConfirmation
        data-testid="saveChangesDialog"
        title="Unsaved Diary Changes"
        okLabel="Save"
        show={showContinueConfirmation}
        onContinue={handleConfirmationSave}
        onCancel={() => {
          setShowContinueConfirmation(false);
          loadDiary();
        }}
      >
        <span>Do you want to save changes?</span>
      </ContinueConfirmation>
      {(isSending || !onInitComplete) && (
        <p className="d-none" role="status">
          loading...
        </p>
      )}
    </>
  );
};

export default SiteDetails;
